import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listAllDistributors,
  getAllGroups,
  getGroupById,
  getTotalActiveGroups,
  updateGroup,
  createNewGroup,
  listAllGroups,
  getUnassignedGroupsToUser,
  getAssignedGroupsToUser,
  updateManyGroups,
  getGroupsEndingSubscriptions,
  getLegalInformation,
  getFirstGroupByVehiculeId,
  getExistingGroup,
  uploadGroupCustomImage,
} from '@/api/group/group';
import { ErrorResponse } from '@/utils/axios.http';
import { TGroupSchema } from '@/schemas/group/group.schema';
import { StructureAdvancedFilter } from '@/entities/structure/group';
import { TCustomQueryProps } from '@/type/hooks.types';
import {
  ListAllDistributor,
  ListAllGroup,
  TotalActiveGroup,
  UploadGroupCustomImageDTO,
} from '@/api/entities/group';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';
import { useAuth } from './auth.hooks';

export const useCreateGroup = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TGroupSchema) => createNewGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingGroup'] });
      queryClient.invalidateQueries({ queryKey: ['listAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['listAllDistributors'] });
      queryClient.invalidateQueries({ queryKey: ['listAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['listAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });

      queryClient.invalidateQueries({
        queryKey: ['getGroupsEndingSubscriptions'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedCompaniesToGroup'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedGroupsToUser'],
      });

      notify(t('common:groupCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetExistingGroup = (query?: TGroupSchema) => {
  return useQuery({
    queryKey: ['getExistingGroup', query],
    queryFn: () => getExistingGroup(query),
  });
};

export const useListAllGroups = (query?: ListAllGroup) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['listAllGroups', query],
    queryFn: () =>
      listAllGroups({ ...query, userId: limitedRoleLoggedInUserId }),
  });
};

export const useGetAllGroups = (
  filter: StructureAdvancedFilter,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  return useQuery({
    queryKey: ['getAllGroups', filter, page, sortBy, sortDirection],
    queryFn: () => getAllGroups(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveGroups = (query?: TotalActiveGroup) => {
  return useQuery({
    queryKey: ['getTotalActiveGroups', query],
    queryFn: () => getTotalActiveGroups(query),
  });
};

export const useGetGroupById = (id: string) => {
  return useQuery({
    queryKey: ['getGroupById', id],
    queryFn: () => getGroupById(id),
    enabled: !!id,
  });
};

export const useGetFirstGroupByVehiculeId = (vehiculeId: string) => {
  return useQuery({
    queryKey: ['getFirstGroupByVehiculeId', vehiculeId],
    queryFn: () => getFirstGroupByVehiculeId(vehiculeId),
    enabled: !!vehiculeId,
  });
};

export const useGetLegalInformation = (user: string, id: string) => {
  return useQuery({
    queryKey: ['getLegalInformation'],
    queryFn: () => getLegalInformation(user, id),
  });
};

export const useListAllDistributors = (query?: ListAllDistributor) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['listAllDistributors', query],
    queryFn: () =>
      listAllDistributors({ ...query, userId: limitedRoleLoggedInUserId }),
  });
};

export const useGetUnassignedGroupsToUser = (userId: string) => {
  return useQuery({
    queryKey: ['getUnassignedGroupsToUser', userId],
    queryFn: () => getUnassignedGroupsToUser(userId),
    enabled: !!userId,
  });
};

export const useGetAssignedGroupsToUser = (userId: string) => {
  return useQuery({
    queryKey: ['getAssignedGroupsToUser', userId],
    queryFn: () => getAssignedGroupsToUser(userId),
    enabled: !!userId,
  });
};

export const useGetGroupsEndingSubscriptions = (userId?: string) => {
  return useQuery({
    queryKey: ['getGroupsEndingSubscriptions', userId],
    queryFn: () => getGroupsEndingSubscriptions(userId),
  });
};

export const useUpdateGroup = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (group: TGroupSchema) => updateGroup(group),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({ queryKey: ['getAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingGroup'] });
      queryClient.invalidateQueries({ queryKey: ['getGroupById', id] });
      queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['listAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['listAllDistributors'] });
      queryClient.invalidateQueries({ queryKey: ['listAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['listAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });

      queryClient.invalidateQueries({
        queryKey: ['getGroupsEndingSubscriptions'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getAssignedCompaniesToGroup'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getAssignedGroupsToUser'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedCompaniesToGroup'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedGroupsToUser'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getResultByVehiculeCheckId'],
      });

      notify(t('common:groupUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating group:', error);
    },
  });
};

export const useUpdateManyGroups = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groups: TGroupSchema[]) => updateManyGroups(groups),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({ queryKey: ['getGroupById', item?.id] });
      }

      queryClient.invalidateQueries({ queryKey: ['getAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveGroups'] });
      queryClient.invalidateQueries({ queryKey: ['getExistingGroup'] });
      queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['listAllGroups'] });
      queryClient.invalidateQueries({ queryKey: ['listAllDistributors'] });
      queryClient.invalidateQueries({ queryKey: ['listAllCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['listAllBranches'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });

      queryClient.invalidateQueries({
        queryKey: ['getGroupsEndingSubscriptions'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getAssignedCompaniesToGroup'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getAssignedGroupsToUser'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedCompaniesToGroup'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getUnassignedGroupsToUser'],
      });

      notify(t('common:groupUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:groupCouldNotBeUpdated'), NOTIFY.ERROR);
      console.error('Error when updating groups:', error);
    },
  });
};

export const useGetGroupsSelectOptions = (query?: ListAllGroup) => {
  const { data: allGroups, isLoading: isLoadingAllGroups } =
    useListAllGroups(query);

  const getGroupsSelectOptions = () => {
    if (!isLoadingAllGroups && allGroups && allGroups.data.length > 0) {
      return allGroups.data.map((group: TGroupSchema) => {
        const { id, name } = group;

        return {
          label: name,
          value: id,
        };
      });
    }

    return undefined;
  };

  return { getGroupsSelectOptions, isLoadingAllGroups };
};

export const useUploadGroupCustomImage = ({
  handleSuccess,
  groupId,
  filename,
}: Partial<
  TCustomQueryProps & { groupId?: string; filename?: string }
> = {}) => {
  return useMutation({
    mutationFn: (
      data: FormData | { filename?: string; formData: FormData }
    ) => {
      // Handle both signatures: FormData directly or { filename, formData }
      let mutateFilename: string | undefined;
      let formData: FormData;

      if (data instanceof FormData) {
        formData = data;
      } else {
        mutateFilename = data.filename;
        formData = data.formData;
      }

      // Backend expects field name 'image', but formData uses 'file'
      // Create new FormData with correct field name
      const file = formData.get('file');
      const correctedFormData = new FormData();
      if (file && typeof file !== 'string') {
        correctedFormData.append('image', file as File | Blob, 'image.jpg');
      }

      const uploadData: UploadGroupCustomImageDTO = {
        groupId: groupId!,
        formData: correctedFormData,
      };
      // Use filename from hook config if provided, otherwise use the one from mutate call
      const finalFilename = filename || mutateFilename || '';
      return uploadGroupCustomImage(finalFilename, uploadData);
    },
    onSuccess: (data) => {
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};
